import React, { useEffect, useState, useCallback } from "react";
import FilterDropdown from "../../components/common/FilterDropdown";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  getAllSingleWorkOut,
  getSinglePlansByTag,
  getSingleWorkoutById,
  uploadSingleWorkout,
  deleteSingleWorkoutById
} from "../../api/singleWorkout";
import AddSinglePlanButton from "./components/AddSinglePlanButton";
import { SinglePlanOverViewInput } from "../../config/singleWorkout";
import SingleWorkoutTable from "./components/SingleWorkoutTable";
import { PERPAGE_RECORD } from "../../utils/isValidImage";
import CustomPagination from "../../components/common/CustomPagination";

const SingleWorkout = () => {
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [error, setError] = useState(null);
  const [uploadedJson, setUploadedJson] = useState(null);
  const [planData, setPlanData] = useState({});
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentModal, setCurrentModal] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");

  const [pagination, setPagination] = useState(null);

  const openModal = (modalNumber) => {
    setCurrentModal(modalNumber);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this single workout?")) {
      try {
        const response = await deleteSingleWorkoutById(id);
        if (response.message) {
          toast.success("single single workout deleted successfully!");
          await fetchPlans();
        } else {
          throw new Error(response.message);
        }
      } catch (error) {
        toast.error("Failed to delete single single workout file.");
        console.error("Error deleting single workout:", error);
      }
    }
  }
  const filterOptions = ["All", "Featured", "Trending"];
  const fetchPlans = useCallback(async (page = 1) => {
    try {
      let data = []
      if (selectedFilter === "All") {
        data = await getAllSingleWorkOut({
          page,
          limit: PERPAGE_RECORD, ...(searchTerm ? {search: searchTerm.toLowerCase()} : {})
        })
        data.pagination.totalPages = Math.ceil(data.pagination.total / data.pagination.perPage)
        setPagination(data.pagination)
      } else {
        data['data'] = await getSinglePlansByTag(selectedFilter);
      }

      setPlans(data?.data);
    } catch (error) {
      setError("Failed to fetch plans");
      console.error(error);
    }
  }, [searchTerm, selectedFilter]);

  useEffect(() => {
    fetchPlans().then();
  }, [selectedFilter, searchTerm, fetchPlans]);


  const convertTimestamp = (createdAtSeconds) => {
    const uploadDate = new Date(createdAtSeconds * 1000);
    return uploadDate.toLocaleDateString();
  };

  const tableHeader = [
    { label: "Sr.No", width: "5%" },
    { label: "Workout Details", width: "40%" },
    { label: "Uploaded on", width: "25%" },
    { label: "Status", width: "15%" },
    { label: "Edit", width: "5%" },
    { label: "Action", width: "5%" },
    { label: "Download", width: "5%" },
  ];

  const handleEditClick = async (planId) => {
    setSelectedPlanId(planId);
    try {
      const data = await getSingleWorkoutById(planId);
      setUploadedJson(data);
      setPlanData(data);
      openModal(2);
    } catch (error) {
      setError(error.message);
      console.error("Failed to load plan:", error.message);
    }
  };
  const showToast = (message, success) => {
    if (success) {
      toast.success(message);
    } else {
      toast.error(message);
    }
  };
  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const fileSizeLimit = 50 * 1024 * 1024;
      if (selectedFile.size > fileSizeLimit) {
        setErrorMessage(
          "File size exceeds the 50MB limit. Please upload a smaller file."
        );
        setFileName(null);
        return;
      }

      const validExtensions = ["json"];
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        setErrorMessage("Please upload a valid JSON file.");
        setFileName(null);
        return;
      }

      setErrorMessage(null);
      setFileName(selectedFile.name);
      setFile(selectedFile);

      const reader = new FileReader();
      reader.onload = () => {
        try {
          const jsonData = JSON.parse(reader.result);
          setUploadedJson(jsonData);
          setPlanData(jsonData);
        } catch (e) {
          setErrorMessage("The uploaded file is not a valid JSON file.");
          setFile(null);
        }
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleImageUrlChange = (url) => {
    setImageUrl(url);
    setPlanData((prevData) => ({
      ...prevData,
      banner_image: url,
    }));
    setUploadedJson((prevJson) => ({
      ...prevJson,
      banner_image: url,
    }));
  };

  const handleVideoUrlChange = (url) => {
    setVideoUrl(url);
    setPlanData((prevData) => ({
      ...prevData,
      workout_video: url,
    }));
    setUploadedJson((prevJson) => ({
      ...prevJson,
      workout_video: url,
    }));
  };
  const handleSubmit = async () => {
    if (!file && !uploadedJson?.id) {
      showToast("No file selected for upload.");
      return;
    }

    try {
      let jsonData = { ...uploadedJson };

      if (!jsonData) {
        jsonData = {};
      }

      if (imageUrl) {
        jsonData.banner_image = imageUrl;
      }
      if (videoUrl) {
        jsonData.workout_video = videoUrl;
      }
      const updatedFile = new File([JSON.stringify(jsonData)], fileName, {
        type: "application/json",
      });

      const formData = new FormData();
      formData.append("file", updatedFile, fileName);
      const response = await uploadSingleWorkout(formData, fileName);
      if (response) {
        showToast(
          `Success! Your Single workout plan has been saved. Refer to the Single workout list for details.`,
          true
        );
        setFile(null);
        setFileName(null);
        setUploadedJson(null);
        setPlanData({});
        setImageUrl("");
        setVideoUrl("");
        navigate("/single-workout");
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      showToast("Failed to upload plan.");
    }
  };


  const toggleStatus = async (plan) => {
    // Create a new object instead of modifying directly (React won't detect updates otherwise)
    const updatedPlan = { ...plan, status: !plan.status };

    const updatedFile = new File([JSON.stringify(updatedPlan)], fileName, {
      type: "application/json",
    });

    const formData = new FormData();
    formData.append("file", updatedFile, fileName);
    const response = await uploadSingleWorkout(formData, fileName);
    if (response) {
      showToast(
        `Success! Your Single workout plan has been saved. Refer to the Single workout list for details.`,
        true
      );
      setFile(null);
      setFileName(null);
      setUploadedJson(null);
      setPlanData({});
      setImageUrl("");
      setVideoUrl("");
      await fetchPlans()
    }

  };


  const handleAddNewPlanClick = () => {
    setSelectedPlanId(null);
  };
  const modalHeading = "Single workouts";
  const modalAddBtn = "Add New Workout";

  const handlePageChange = (page) => {
    fetchPlans(page).then();
  };

  return (
    <div className="right-side-wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-6">
            <h2>{modalHeading}</h2>
          </div>
          <div className="col-sm-6">
            <div className="btn-sec">
              <input
                type="text"
                placeholder="Search plans..."
                className="form-control"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FilterDropdown
                filterOptions={filterOptions}
                onFilterChange={setSelectedFilter}
              />{" "}
              <AddSinglePlanButton
                setCurrentModal={setCurrentModal}
                currentModal={currentModal}
                openModal={openModal}
                modalAddBtn={modalAddBtn}
                PlanOverViewInput={SinglePlanOverViewInput}
                selectedPlanId={selectedPlanId}
                handleFileUpload={handleFileUpload}
                handleSubmit={handleSubmit}
                uploadedJson={uploadedJson}
                planData={planData}
                errorMessage={errorMessage}
                fileName={fileName}
                onAddNewPlanClick={handleAddNewPlanClick}
                setUploadedJson={setUploadedJson}
                handleImageUrlChange={handleImageUrlChange}
                handleVideoUrlChange={handleVideoUrlChange}
                imageUrl={imageUrl}
                videoUrl={videoUrl}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <SingleWorkoutTable
            header={tableHeader}
            plans={plans}
            error={error}
            convertTimestamp={convertTimestamp}
            onEditClick={handleEditClick}
            handleDelete={handleDelete}
            toggleStatus={toggleStatus}
          />
        </div>

        <CustomPagination pagination={pagination} handlePageChange={handlePageChange} />

      </div>
    </div>
  );
};

export default SingleWorkout;
